/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

import { IS_HRWIZ_CONSOLE_ENABLED } from '../../../constants/env';
import { ADDITIONAL_ROLES_ORG, TEAMS_APPLICATIONS } from '../../../constants/main';
import { NAV_DASHBOARD, NAV_LIVECHAT, NAV_LOGOUT } from '../../../constants/navigation';
import { setOrganization } from '../../../store/auth/authActions';
import is from '../../is';
import replaceUrlParams from '../../string/replaceUrlParams';

/**
 * Callback function after a user logins through Teams
 *
 * @param {Object} loginResponse Login response object with user info.
 * @param {Function} navigate Navigate hook function to redirect.
 * @param {Object} location Current location state object.
 * @param {Function} dispatch Store dispatch function.
 * @returns {Function} Redirects the user to the correct app.
 */
export const teamsAppLogin = async (loginResponse, navigate, location, dispatch) => {
  const urlSearchParams = new URLSearchParams(location.search);
  /*
  * MS Teams or web HRwiz login handling
  */
  if (IS_HRWIZ_CONSOLE_ENABLED) {
    // Get the first available organization
    const hrWizOrgId = loginResponse.user.organizations[0]?.id;
    /*
    * If the user is not in any organization log them out
    */
    if (!hrWizOrgId) {
      return navigate(NAV_LOGOUT);
    }
    await dispatch(setOrganization(hrWizOrgId));
    const redirectUrl = urlSearchParams.get('next');
    if (redirectUrl) {
      return navigate(redirectUrl);
    }
    return navigate(replaceUrlParams(NAV_DASHBOARD, { orgId: hrWizOrgId }));
  }
  /*
  * MS Teams Live-Chat login handling
  */
  if (is.inMicrosoftTeams && urlSearchParams.get('app') === TEAMS_APPLICATIONS.LIVECHAT) {
    const { organizationRoles } = loginResponse.user.effectiveRoles;
    let livechatOrgId;
    // Check for the organization live-agent role
    for (const [orgId, roles] of Object.entries(organizationRoles ?? {})) {
      if (roles.includes(ADDITIONAL_ROLES_ORG.LIVE_AGENT)) {
        livechatOrgId = orgId;
        break;
      }
    }
    /*
    * If the user is not a live-agent in any
    * available organization log them out
    */
    if (!livechatOrgId) {
      return navigate(NAV_LOGOUT);
    }
    await dispatch(setOrganization(livechatOrgId, navigate));
    return navigate(replaceUrlParams(NAV_LIVECHAT, { orgId: livechatOrgId }));
  }
};
